import { isNativePlatform, getPlatform } from './nativeSocialAuth';

// RevenueCat wraps StoreKit / Play Billing. Required by App Review Guideline 3.1.1 for
// any subscription sold inside the app; Stripe continues to serve the web.
//
// Everything here is best-effort and fails soft. Billing is not load-bearing for using
// Agence: a user whose purchase machinery is broken should still get the app, and the
// SERVER is the entitlement authority regardless (the RevenueCat webhook writes
// users.iap_plan). Nothing this module returns is trusted for gating — it drives UI only.

// Public SDK keys. These ship in the app bundle by design; RevenueCat issues separate
// publishable keys per platform and they are not secrets.
const API_KEYS = {
  ios: process.env.REACT_APP_RC_API_KEY_IOS,
  android: process.env.REACT_APP_RC_API_KEY_ANDROID,
};

// Must match the RevenueCat dashboard exactly, and the server's RC_ENTITLEMENT. A
// mismatch fails silently — nobody is ever entitled, with nothing in the logs. That
// already happened once here (`plus` vs `agence_plus`, 2026-09-18).
export const PLUS_ENTITLEMENT = 'agence_plus';

// RevenueCat's onboarding flow hands out a `test_…` key for its simulated Test Store.
// It configures happily and then no purchase ever reaches StoreKit or Play Billing, with
// no error — so treat it as "not configured" rather than shipping a dead paywall.
const TEST_STORE_PREFIX = 'test_';

export function purchasesApiKey() {
  const key = API_KEYS[getPlatform()];
  if (!key || key.startsWith(TEST_STORE_PREFIX)) return null;
  return key;
}

export function isPurchasesAvailable() {
  return isNativePlatform() && !!purchasesApiKey();
}

// Never return or resolve with a Capacitor plugin proxy. A proxy answers ANY property
// access with a function that forwards to native, including `then` — so the promise
// machinery sees a thenable and calls `Purchases.then(resolve, reject)`, which forwards
// to native and rejects. That exact bug broke native Google sign-in; see
// nativeSocialAuth.js's ensureGoogleInitialized().
async function loadPlugin() {
  const mod = await import('@revenuecat/purchases-capacitor');
  return { Purchases: mod.Purchases };
}

let configurePromise = null;
let configuredUserId = null;

/**
 * Configure the SDK and identify the user. Safe to call on every auth state change.
 *
 * Always identifies with OUR user id rather than letting RevenueCat assign an anonymous
 * one, so the webhook's `app_user_id` maps straight to a row in `users` — the server
 * ignores anonymous ids because it has nothing to attribute them to.
 */
export async function configurePurchases(appUserID) {
  if (!isPurchasesAvailable() || !appUserID) return;

  if (configurePromise) {
    await configurePromise;
    // Already configured for someone else (or logged out): switch identity rather than
    // reconfigure, which the SDK does not support and which would leave the previous
    // account's entitlements attached.
    if (configuredUserId !== appUserID) {
      try {
        const { Purchases } = await loadPlugin();
        await Purchases.logIn({ appUserID });
        configuredUserId = appUserID;
      } catch {
        // Leave configuredUserId alone so a later attempt retries.
      }
    }
    return;
  }

  configurePromise = (async () => {
    const { Purchases } = await loadPlugin();
    await Purchases.configure({ apiKey: purchasesApiKey(), appUserID });
    configuredUserId = appUserID;
  })();

  try {
    await configurePromise;
  } catch {
    // Do NOT cache a failed configure as success — a transient failure at launch would
    // otherwise disable purchasing for the whole session.
    configurePromise = null;
    configuredUserId = null;
  }
}

/**
 * Called on sign-out. Grocerly never does this, which is a real account-switching gap
 * there: the next account signing in on the same device inherits the previous one's
 * RevenueCat identity.
 */
export async function logOutPurchases() {
  if (!configurePromise) return;
  try {
    const { Purchases } = await loadPlugin();
    await Purchases.logOut();
  } catch {
    // Sign-out must complete regardless — see AuthContext.logout()'s comment on why
    // cleanup is never allowed to gate it.
  }
  configuredUserId = null;
}

/**
 * Whether this product actually carries a free trial, right now, for this user's store.
 *
 * Trials live at the store rather than in RevenueCat, so this must be read off the product
 * — never inferred from the fact that a trial was configured in a console. A hardcoded
 * promise would also lie to a returning user who is no longer trial-eligible.
 *
 * The two platforms express it differently, and checking only the iOS shape was a real bug
 * (fixed 2026-09-19): on **Google Play** a trial is a zero-amount pricing phase hanging off
 * a SubscriptionOption, and `introPrice` can be null entirely, so an Android trial would
 * silently never be advertised. `defaultOption`/`subscriptionOptions` are documented
 * "Google Play only", hence checking all three shapes rather than branching on platform.
 */
function productHasFreeTrial(product) {
  if (!product) return false;
  if (product.introPrice?.price === 0) return true;              // App Store
  if (product.defaultOption?.freePhase) return true;             // Play, default base plan
  return Array.isArray(product.subscriptionOptions)              // Play, any other option
    && product.subscriptionOptions.some(option => option?.freePhase);
}

const PERIOD_UNITS = { D: 'day', W: 'week', M: 'month', Y: 'year' };
const PACKAGE_TYPE_PERIODS = {
  WEEKLY: 'week', MONTHLY: 'month', TWO_MONTH: '2 months',
  THREE_MONTH: '3 months', SIX_MONTH: '6 months', ANNUAL: 'year',
};

/**
 * The billing period as display text ("month", "3 months"), or null when unknown.
 *
 * Guideline 3.1.2(c) (rejection 2026-09-30) requires the subscription length in the app.
 * The store's own ISO 8601 `subscriptionPeriod` ("P1M") is read first because it is what the
 * user is actually billed on; RevenueCat's `packageType` is the fallback. Returns null
 * rather than guessing — an invented length is worse than none.
 */
function billingPeriod(rcPackage) {
  const match = /^P(\d+)([DWMY])$/.exec(rcPackage?.product?.subscriptionPeriod || '');
  if (match) {
    const count = Number(match[1]);
    const unit = PERIOD_UNITS[match[2]];
    return count === 1 ? unit : `${count} ${unit}s`;
  }
  return PACKAGE_TYPE_PERIODS[rcPackage?.packageType] || null;
}

function normalizePackage(rcPackage) {
  const product = rcPackage?.product || {};
  return {
    identifier: rcPackage?.identifier,
    packageType: rcPackage?.packageType || null,
    priceString: product.priceString,
    // Numeric price + currency so a saving is computed per storefront (lib/storePricing.js);
    // per-month is the SDK's own string, formatted in the store currency.
    price: typeof product.price === 'number' ? product.price : null,
    currencyCode: product.currencyCode || null,
    pricePerMonthString: product.pricePerMonthString || null,
    period: billingPeriod(rcPackage),
    title: product.title,
    hasFreeTrial: productHasFreeTrial(product),
    introPrice: product.introPrice || null,
    rcPackage,
  };
}

/**
 * The offering marked Current in RevenueCat, normalized for the paywall.
 * Returns null on any failure — the caller shows a "couldn't load plans" state.
 */
export async function getCurrentOffering() {
  if (!isPurchasesAvailable()) return null;
  try {
    const { Purchases } = await loadPlugin();
    const result = await Purchases.getOfferings();
    // The payload has appeared as both `{ offerings: { current } }` and `{ current }`
    // across SDK versions. Destructuring `{ offerings }` silently broke Grocerly's
    // paywall, so accept both rather than pinning one.
    const offerings = result?.offerings ?? result;
    const current = offerings?.current;
    if (!current) return null;
    return {
      identifier: current.identifier,
      packages: (current.availablePackages || []).map(normalizePackage),
    };
  } catch {
    return null;
  }
}

function isEntitled(customerInfo) {
  return Boolean(customerInfo?.entitlements?.active?.[PLUS_ENTITLEMENT]);
}

// RevenueCat reports a user-initiated cancellation as an error. It is a normal outcome,
// not something to show an error banner for.
function isUserCancellation(err) {
  return err?.userCancelled === true
    || err?.code === '1'
    || err?.code === 1
    || /cancel/i.test(err?.message || '');
}

/**
 * @returns {Promise<{status: 'purchased'|'cancelled'|'error', message?: string}>}
 */
export async function purchasePlus(pkg) {
  try {
    const { Purchases } = await loadPlugin();
    const res = await Purchases.purchasePackage({ aPackage: pkg.rcPackage });
    // A completed transaction that did not yield the entitlement means something is
    // misconfigured (wrong entitlement id, product not attached). Reporting success
    // would produce a support ticket nobody can answer.
    if (!isEntitled(res?.customerInfo)) {
      return { status: 'error', message: 'Purchase completed but the subscription did not activate. Try Restore Purchases.' };
    }
    return { status: 'purchased' };
  } catch (err) {
    if (isUserCancellation(err)) return { status: 'cancelled' };
    return { status: 'error', message: err?.message || 'Purchase failed.' };
  }
}

/**
 * Apple REQUIRES a Restore Purchases control, and requires that "nothing found" be
 * stated explicitly rather than failing silently.
 * @returns {Promise<{status: 'restored'|'none'|'error', message?: string}>}
 */
export async function restorePurchases() {
  try {
    const { Purchases } = await loadPlugin();
    const res = await Purchases.restorePurchases();
    return isEntitled(res?.customerInfo) ? { status: 'restored' } : { status: 'none' };
  } catch (err) {
    return { status: 'error', message: err?.message || 'Could not restore purchases.' };
  }
}
